import { formatConstantCaseToSentenceCase, pluralizeWithValue } from '../string/string';

/** Which fact a strip segment states. Each app maps a kind to its own icon. */
export type EnvBannerFactKind =
  | 'local-app'
  | 'preview-app'
  | 'database'
  | 'prod-database'
  | 'unknown-database'
  | 'prod-api'
  | 'api-host'
  | 'restore-copy'
  | 'restore-unknown'
  | 'restore-unavailable';

export type EnvBannerFact = {
  kind: EnvBannerFactKind;
  text: string;
};

/** Rounded age of a database stamp. `compact` is the hover card (`12h`); `words` is the strip (`12 hours`). */
type DataAge = {
  compact: string;
  words: string;
};

const MINUTE_MS = 60_000;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;

const DATABASE_LABEL: Record<string, string> = {
  prod: 'Prod DB',
  localhost: 'Local DB',
  dev: 'Dev DB',
  staging: 'Staging DB',
  preview: 'Preview DB',
  qa: 'QA DB',
  test: 'Test DB'
};

/** Production API host. A build pointed here writes to production. */
export const PROD_API_HOSTNAME = 'api.noddi.co';

/** Banner and dev-settings page share one refetch clock, including the worker app. */
export const DEV_SETTINGS_STALE_TIME_MS = 30_000;

type RestoreStamp = {
  sourceEnvironment: string;
  dataAsOf: string;
};

type RestoreDatabase = {
  environment: string | null;
  restoreStampStatus: string | null;
  restoreStamp: RestoreStamp | null;
};

/**
 * Age of a stamp against `now`. Null when the stamp is unreadable or in the
 * future, so a skewed clock drops the age instead of rendering a negative one.
 */
export const dataAge = (dataAsOf: string, now: Date = new Date()): DataAge | null => {
  const parsed = new Date(dataAsOf);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  const minutes = Math.floor((now.getTime() - parsed.getTime()) / MINUTE_MS);
  if (minutes < 0) {
    return null;
  }
  if (minutes < MINUTES_PER_HOUR) {
    return age(minutes, 'minute', 'm');
  }

  const hours = Math.floor(minutes / MINUTES_PER_HOUR);
  if (hours < HOURS_PER_DAY) {
    return age(hours, 'hour', 'h');
  }
  return age(Math.floor(hours / HOURS_PER_DAY), 'day', 'd');
};

/** The database fact. Prod is its own kind because it is the live database. */
export const databaseFact = (environment: string): EnvBannerFact => ({
  kind: environment === 'prod' ? 'prod-database' : 'database',
  text: DATABASE_LABEL[environment] ?? `${formatConstantCaseToSentenceCase(environment)} DB`
});

/**
 * The restore fact. Null for a prod database: that database is live, not a copy.
 * A missing stamp and a failed read stay distinct so the strip can say which.
 */
export const restoreCopyFact = (database: RestoreDatabase, now: Date = new Date()): EnvBannerFact | null => {
  if (database.environment === 'prod') {
    return null;
  }
  if (database.restoreStampStatus === 'error') {
    return { kind: 'restore-unavailable', text: 'Restore unavailable' };
  }
  if (!database.restoreStamp) {
    return { kind: 'restore-unknown', text: 'Restore unknown' };
  }

  const source = formatConstantCaseToSentenceCase(database.restoreStamp.sourceEnvironment);
  const ageWords = dataAge(database.restoreStamp.dataAsOf, now)?.words;
  return {
    kind: 'restore-copy',
    text: ageWords ? `${source} copy ${ageWords} old` : `${source} copy`
  };
};

export const apiHostname = (apiUrl: string | null): string | null => {
  if (!apiUrl) {
    return null;
  }
  try {
    return new URL(apiUrl).hostname;
  } catch {
    return null;
  }
};

const age = (count: number, unit: string, compactUnit: string): DataAge => ({
  compact: `${count}${compactUnit}`,
  words: pluralizeWithValue({ value: count, word: unit })
});
